import React, { useState } from "react";
import { Collapse } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ToolConfig, ToolGroupConfig } from "./dashboardTypes";


/*  SectionLabel  */
export const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 32 }}>
    <h2
      style={{
        margin: 0,
        fontSize: 11,
        fontWeight: 700,
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: "0.09em",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </h2>
    <div
      style={{
        flex: 1,
        height: 1,
        background: "linear-gradient(90deg, #e2e8f0 0%, transparent 100%)",
      }}
    />
  </div>
);

/* ── BackButton ── */
export const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: "none",
        border: "1px solid rgba(0,0,0,0.10)",
        borderRadius: 10,
        padding: "7px 16px",
        fontSize: 13,
        color: "#64748b",
        cursor: "pointer",
        transition: "all 0.15s",
        fontFamily: "inherit",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(0,0,0,0.2)";
        (e.currentTarget as HTMLButtonElement).style.color = "#0f172a";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(0,0,0,0.10)";
        (e.currentTarget as HTMLButtonElement).style.color = "#64748b";
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M11.5 7H2.5M2.5 7L6 3.5M2.5 7L6 10.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {t("dashboard.back")}
    </button>
  );
};

/* ── PrimaryButton ── */
export const PrimaryButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  accentColor: string;
}> = ({ children, onClick, accentColor }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: "100%",
        padding: "14px 24px",
        borderRadius: 14,
        border: "none",
        background: accentColor,
        color: "#fff",
        fontSize: 15,
        fontWeight: 700,
        cursor: "pointer",
        transition: "box-shadow 0.2s ease, opacity 0.15s ease",
        boxShadow: hov ? `0 8px 24px ${accentColor}55` : `0 2px 8px ${accentColor}22`,
        fontFamily: "inherit",
        letterSpacing: "-0.01em",
        opacity: hov ? 0.9 : 1,
      }}
    >
      {children}
    </button>
  );
};

/* ── ParamCard ── */
export const ParamCard: React.FC<{
  name: string;
  desc: string;
  required: boolean;
  accentColor: string;
}> = ({ name, desc, required, accentColor }) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e9eef5",
        borderRadius: 14,
        padding: "18px 20px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 7,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{name}</span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            padding: "3px 9px",
            borderRadius: 100,
            background: required ? `${accentColor}18` : "#f1f5f9",
            color: required ? accentColor : "#94a3b8",
            border: `1px solid ${required ? accentColor + "33" : "#e2e8f0"}`,
            flexShrink: 0,
            marginLeft: 8,
            letterSpacing: "0.03em",
          }}
        >
          {required ? t("dashboard.required") : t("dashboard.optional")}
        </span>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.65 }}>{desc}</p>
    </div>
  );
};

/* ── StepRow ── */
export const StepRow: React.FC<{
  title: string;
  index: number;
  accentColor: string;
}> = ({ title, index, accentColor }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 16,
      padding: "14px 0",
      borderBottom: "1px solid #f1f5f9",
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        background: `${accentColor}15`,
        border: `1.5px solid ${accentColor}35`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 700,
        color: accentColor,
        flexShrink: 0,
        marginTop: 1,
      }}
    >
      {index + 1}
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", paddingTop: 5 }}>
      {title}
    </div>
  </div>
);

/* ── StatCard ── */
export const StatCard: React.FC<{ sc: any; onClick?: () => void }> = ({ sc, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "24px 22px",
        borderRadius: "16px",
        background: hovered ? sc.bg : sc.bg.replace("0.08", "0.04"),
        border: `1px solid ${hovered ? sc.border : "rgba(0,0,0,0.06)"}`,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.22s ease",
        transform: hovered && onClick ? "translateY(-3px)" : "translateY(0)",
        boxShadow: hovered && onClick ? `0 8px 24px ${sc.bg}` : "none",
      }}
    >
      <div
        style={{
          fontSize: "30px",
          fontWeight: 800,
          color: sc.color,
          letterSpacing: "-0.03em",
          lineHeight: 1,
          marginBottom: "8px",
        }}
      >
        {sc.value}
      </div>
      <div
        style={{
          fontSize: "11px",
          fontWeight: 600,
          color: "#94a3b8",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {sc.label}
      </div>
    </div>
  );
};

/* ── CollapsibleSection ── */
export const CollapsibleSection: React.FC<{
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  accentColor?: string;
}> = ({ title, subtitle, defaultOpen = false, children, accentColor = "#4968A2" }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{ marginBottom: "20px" }}>
      <div
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "22px 32px",
          background: hovered
            ? `linear-gradient(160deg, #ffffff 55%, ${accentColor}0d 100%)`
            : "#ffffff",
          borderRadius: open ? "18px 18px 0 0" : "18px",
          border: `1.5px solid ${hovered ? accentColor + "50" : "rgba(0,0,0,0.07)"}`,
          cursor: "pointer",
          userSelect: "none",
          transition: "all 0.25s ease",
          boxShadow: hovered
            ? `0 0 0 4px ${accentColor}14, 0 8px 24px rgba(0,0,0,0.08)`
            : "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{
              width: "3px",
              height: "36px",
              borderRadius: "0 3px 3px 0",
              background: accentColor,
              flexShrink: 0,
              opacity: hovered ? 1 : 0.4,
              transition: "opacity 0.2s ease",
            }}
          />
          <div>
            <p
              style={{
                margin: 0,
                fontSize: "15px",
                fontWeight: 700,
                color: "#0f172a",
                letterSpacing: "-0.02em",
              }}
            >
              {title}
            </p>
            {subtitle && (
              <p style={{ margin: "3px 0 0 0", fontSize: "12px", color: "#94a3b8", fontWeight: 400 }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>
        <div
          style={{
            width: "30px",
            height: "30px",
            borderRadius: "8px",
            background: hovered ? `${accentColor}15` : "rgba(0,0,0,0.04)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 0.3s ease, background 0.2s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            flexShrink: 0,
            border: `1px solid ${hovered ? accentColor + "30" : "transparent"}`,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path
              d="M3 5L7 9L11 5"
              stroke={hovered ? accentColor : "#94a3b8"}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      <Collapse in={open} timeout={300}>
        <div
          style={{
            background: "rgba(255,255,255,0.97)",
            border: `1.5px solid ${accentColor}25`,
            borderTop: "none",
            borderRadius: "0 0 18px 18px",
            padding: "32px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          }}
        >
          {children}
        </div>
      </Collapse>
    </div>
  );
};

/* ── ToolCard ── */
export const ToolCard: React.FC<{ tool: ToolConfig; onSelect: () => void }> = ({
  tool,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  const features = t(`dashboard.tools.${tool.id}.features`, {
    returnObjects: true,
  }) as string[];
  const stepCount = (
    t(`dashboard.tools.${tool.id}.steps`, { returnObjects: true }) as any[]
  ).length;

  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered
          ? `linear-gradient(160deg, #ffffff 55%, ${tool.accentColor}0d 100%)`
          : "#ffffff",
        border: `1.5px solid ${hovered ? tool.accentColor + "50" : "rgba(0,0,0,0.07)"}`,
        borderRadius: 24,
        padding: "40px 36px 32px",
        cursor: "pointer",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease, background 0.25s ease",
        boxShadow: hovered
          ? `0 0 0 4px ${tool.accentColor}14, 0 12px 36px rgba(0,0,0,0.10)`
          : "0 2px 8px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        height: 480,
        minWidth: 0,
      }}
    >
      {/* accent bar */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 36,
          bottom: 36,
          width: 3,
          borderRadius: "0 3px 3px 0",
          background: tool.accentColor,
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.2s ease",
        }}
      />
      {/* glow */}
      <div
        style={{
          position: "absolute",
          top: -40,
          right: -40,
          width: 180,
          height: 180,
          borderRadius: "50%",
          background: tool.accentColor,
          opacity: hovered ? 0.08 : 0.03,
          filter: "blur(32px)",
          transition: "opacity 0.25s ease",
          pointerEvents: "none",
        }}
      />

      {/* tag */}
      <span
        style={{
          display: "inline-block",
          padding: "5px 14px",
          borderRadius: 100,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.05em",
          background: hovered ? tool.tagBg : "rgba(0,0,0,0.04)",
          color: hovered ? tool.tagColor : "#94a3b8",
          marginBottom: 24,
          alignSelf: "flex-start",
          border: `1px solid ${hovered ? tool.tagColor + "40" : "transparent"}`,
          transition: "all 0.2s ease",
        }}
      >
        {t(`dashboard.tools.${tool.id}.tag`)}
      </span>

      {/* icon */}
      <div
        style={{
          fontSize: 52,
          lineHeight: 1,
          marginBottom: 22,
          transition: "transform 0.2s ease",
          transform: hovered ? "scale(1.1)" : "scale(1)",
          display: "inline-block",
          alignSelf: "flex-start",
          transformOrigin: "left center",
        }}
      >
        {tool.icon}
      </div>

      {/* title */}
      <h3
        style={{
          margin: "0 0 0",
          fontSize: 21,
          fontWeight: 700,
          color: "#0f172a",
          letterSpacing: "-0.025em",
          lineHeight: 1.3,
        }}
      >
        {t(`dashboard.tools.${tool.id}.title`)}
      </h3>

      {/* short desc */}
      <p
        style={{
          margin: 0,
          fontSize: 13.5,
          color: "#64748b",
          lineHeight: 1.65,
          opacity: hovered ? 0 : 1,
          transition: "opacity 0.15s ease",
          position: "absolute",
          top: 260,
          left: 36,
          right: 36,
          overflowWrap: "break-word",
        }}
      >
        {t(`dashboard.tools.${tool.id}.descShort`)}
      </p>

      {/* features (hover) */}
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.2s ease 0.05s",
          position: "absolute",
          top: 252,
          bottom: 76,
          left: 36,
          right: 36,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          minWidth: 0,
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: 4,
          scrollbarWidth: "thin",
          scrollbarColor: `${tool.accentColor}55 transparent`,
        }}
      >
        {features.map((f, i) => (
          <li
            key={i}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 9,
              fontSize: 13,
              color: "#475569",
              lineHeight: 1.5,
              minWidth: 0,
              overflowWrap: "break-word",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: tool.accentColor,
                marginTop: 6,
                flexShrink: 0,
              }}
            />
            {f}
          </li>
        ))}
      </ul>

      {/* footer */}
      <div
        style={{
          position: "absolute",
          bottom: 28,
          left: 36,
          right: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: hovered ? tool.tagColor : "#94a3b8",
            transition: "color 0.2s ease",
            display: "flex",
            alignItems: "center",
            gap: 5,
            minWidth: 0,
            overflowWrap: "break-word",
          }}
        >
          {t("dashboard.selectTool")}
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            style={{
              transform: hovered ? "translateX(4px)" : "translateX(0)",
              transition: "transform 0.2s ease",
            }}
          >
            <path
              d="M2.5 7H11.5M11.5 7L8 3.5M11.5 7L8 10.5"
              stroke={hovered ? tool.tagColor : "#94a3b8"}
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span
          style={{
            fontSize: 11,
            color: "#94a3b8",
            background: "rgba(0,0,0,0.04)",
            padding: "3px 10px",
            borderRadius: 100,
            flexShrink: 0,
          }}
        >
          {t("dashboard.stepsUnit", { count: stepCount })}
        </span>
      </div>
    </div>
  );
};

/* ── ToolGroupCard ── */
export const ToolGroupCard: React.FC<{
  group: ToolGroupConfig;
  onSelect: (group: ToolGroupConfig) => void;
}> = ({ group, onSelect }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={() => onSelect(group)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered
          ? `linear-gradient(160deg, #ffffff 55%, ${group.accentColor}0d 100%)`
          : "#ffffff",
        border: `1.5px solid ${hovered ? group.accentColor + "50" : "rgba(0,0,0,0.07)"}`,
        borderRadius: 24,
        padding: "34px 32px 30px",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease, background 0.25s ease",
        boxShadow: hovered
          ? `0 0 0 4px ${group.accentColor}14, 0 12px 36px rgba(0,0,0,0.10)`
          : "0 2px 8px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        minHeight: 320,
        minWidth: 0,
        cursor: "pointer",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 34,
          bottom: 34,
          width: 3,
          borderRadius: "0 3px 3px 0",
          background: group.accentColor,
          opacity: hovered ? 1 : 0.55,
          transition: "opacity 0.2s ease",
        }}
      />
      <span
        style={{
          display: "inline-block",
          padding: "5px 14px",
          borderRadius: 100,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.05em",
          background: hovered ? group.tagBg : "rgba(0,0,0,0.04)",
          color: hovered ? group.tagColor : "#94a3b8",
          marginBottom: 22,
          alignSelf: "flex-start",
          border: `1px solid ${hovered ? group.tagColor + "40" : "transparent"}`,
          transition: "all 0.2s ease",
        }}
      >
        {t(`dashboard.toolGroups.${group.id}.tag`)}
      </span>

      <div style={{ fontSize: 50, lineHeight: 1, marginBottom: 20 }}>
        {group.icon}
      </div>
      <h3
        style={{
          margin: "0 0 10px",
          fontSize: 23,
          fontWeight: 800,
          color: "#0f172a",
          letterSpacing: "-0.025em",
          lineHeight: 1.25,
        }}
      >
        {t(`dashboard.toolGroups.${group.id}.title`)}
      </h3>
      <p
        style={{
          margin: "0 0 24px",
          fontSize: 13.5,
          color: "#64748b",
          lineHeight: 1.65,
          overflowWrap: "break-word",
        }}
      >
        {t(`dashboard.toolGroups.${group.id}.desc`)}
      </p>

      <span
        style={{
          marginTop: "auto",
          fontSize: 13,
          fontWeight: 800,
          color: hovered ? group.tagColor : "#94a3b8",
          transition: "color 0.2s ease",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {t("dashboard.chooseCategory")}
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          style={{
            transform: hovered ? "translateX(4px)" : "translateX(0)",
            transition: "transform 0.2s ease",
          }}
        >
          <path
            d="M2.5 7H11.5M11.5 7L8 3.5M11.5 7L8 10.5"
            stroke={hovered ? group.tagColor : "#94a3b8"}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </div>
  );
};
